import { createColumnHelper } from "@tanstack/react-table";

import CompanyLogo from "~/components/CompanyLogo";
import Tooltip from "~/components/Tooltip";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type {
	UnprocessedInvoicesIndexTableData,
	UnprocessedInvoicesIndexTableMeta,
} from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { projectNumberSortingFunction } from "~/modules/billing/utils/billingUtils.ts";
import { formatNumberToCurrency } from "~/utils/currencyUtils.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";
import { customArrayIncludesSome } from "~/utils/tanStackTableUtils.ts";

const columnHelper = createColumnHelper<UnprocessedInvoicesIndexTableData>();

export const draftInvoicesIndexTableColumnDefs = [
	columnHelper.accessor("companyDisplayName", {
		header: "Kunde",
		meta: {
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const companyName = props.getValue();
			const rowData = props.row.original;
			return <div className="flex items-center gap-x-2">
				{rowData.companyAvatarImage && <CompanyLogo image={rowData.companyAvatarImage}
															sizeClassName="size-6" />}
				<div className="truncate">{companyName}</div>
			</div>;
		},
	}),
	columnHelper.accessor("projectNumberWithPhaseNumber", {
		header: "Projekt-ID",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "10rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
		sortingFn: projectNumberSortingFunction
	}),
	columnHelper.accessor("projectTitle", {
		header: "Titel",
		meta: {
			headerTextAlign: "left",
			cellTextAlign: "left",
			width: "14rem",
		},
		cell: (props) => props.getValue() || EMPTY_VALUE_PLACEHOLDER,
	}),

	columnHelper.accessor("invoiceTotalCents", {
		header: "Rechnungsbetrag (netto)",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "right",
			width: "10rem",
		},
		cell: (props) => {
			return <div className="font-bold">{formatNumberToCurrency(props.getValue())} €</div>;
		},
		footer: (props) => {
			const total = props.table.getFilteredRowModel().rows.reduce((sum, row) => {
				return sum + (row.original.invoiceTotalCents || 0);
			}, 0);
			return <div className="flex w-full items-center justify-between text-sm font-bold"><span>&sum;</span><span>{formatNumberToCurrency(total)} €</span></div>;
		},
	}),
	columnHelper.accessor("deliveryStartDate", {
		header: "Leistungszeitraum",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
			width: "10rem",
		},
		cell: (props) => {
			const rowData = props.row.original;
			if (!rowData.deliveryStartDate || !rowData.deliveryEndDate) return EMPTY_VALUE_PLACEHOLDER;
			return formatSmartDateRange(rowData.deliveryStartDate, rowData.deliveryEndDate, {
				month: "MMM",
				year: " yy",
			});
		},
	}),
	columnHelper.accessor("comment", {
		header: "Kommentar",
		meta: {
			headerTextAlign: "center",
			cellTextAlign: "center",
		},
		cell: (props) => {
			const comment = props.getValue();
			const { onUpdateCommentClick } = props.table.options.meta as UnprocessedInvoicesIndexTableMeta;

			const Button = <button onClick={(event) => onUpdateCommentClick(event, props.row)}
								   className="flex w-full items-center justify-center gap-x-2 rounded-lg px-2 py-1 text-left hover:bg-yellow-200">
				<div className="truncate font-bold">{comment || EMPTY_VALUE_PLACEHOLDER}</div>
			</button>;

			if (!comment) {
				return Button;
			}

			return <Tooltip text={comment}
							className="max-w-96 whitespace-pre-wrap break-words">
				{Button}
			</Tooltip>;
		},
		filterFn: customArrayIncludesSome,
	}),
];